import React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { PageLayout } from '@lifeomic/chroma-react/components/PageLayout';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { Menu, MenuItem } from '@lifeomic/chroma-react/components/Menu';
import { IconButton } from '@lifeomic/chroma-react/components/IconButton';
import { Link } from '@lifeomic/chroma-react/components/Link';
import {
  ExternalLink,
  Menu as MenuIcon,
  Maximize,
  Minimize,
  RefreshCw,
  SparkleCircle,
  X,
} from '@lifeomic/chromicons';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { ButtonUnstyled } from '@lifeomic/chroma-react/components/ButtonUnstyled';
import { DotLoader } from '@lifeomic/chroma-react/components/DotLoader';
import {
  Radio,
  RadioGroupMinimal,
} from '@lifeomic/chroma-react/components/Radio';
import { useSelector } from 'react-redux';
import { useMaskedMode } from '@lifeomic/phc-web-toolkit/src/hooks/useMaskedMode';
import EnforceAccountFeatureToggle from '@lifeomic/phc-web-toolkit/src/components/EnforceAccountFeatureToggle';
import {
  useActiveAccountId,
  useFeatures,
} from '@lifeomic/phc-web-toolkit/src/hooks';
import { useOmnibarContext } from '@lifeomic/phc-web-toolkit/src/components/Omnibar/OmnibarProvider';
import { selectActiveProject } from '@lifeomic/phc-web-toolkit/src/redux/modules/projectPicker/selectors';
import urls from '../../../../util/urls';
import { Resizable } from 'react-resizable';
import 'react-resizable/css/styles.css';
import { Chat } from '@lifeomic/phc-ui-qa-agent/src/components/Chat';
import { MemberChatHistory } from '@lifeomic/phc-ui-qa-agent/src/components/MemberChatHistory';
import { AttachmentViewerProvider } from '@lifeomic/phc-web-toolkit/src/components/Messaging/AttachmentViewer';
import { AnnualReportContent } from './AnnualReport';
import { ActionPlanContent } from './ActionPlan';
import { ZoriReportsContent } from './AiTools';
import { DocumentsContent } from './Documents';
import { LabsContent } from './Labs';
import { OmicsExplorerContent } from './OmicsExplorer';
import { SurveysContent } from './Surveys';
import { memberDetailMessages } from './messages';
import { MemberSection, MEMBER_SECTION_STORAGE_KEY } from './types';
import { useMemberDetailData } from './useMemberDetailData';
import { useMemberDetailOmnibarSuggestions } from './useMemberDetailOmnibarSuggestions';
import { useMemberDetailStyles } from './styles';
const useStyles = useMemberDetailStyles;

const OPT_IN_STORAGE_KEY = 'fountainLifeUX.optedIn';

export interface MemberDetailProps {
  section?: MemberSection;
}

const MemberDetail: React.FC<MemberDetailProps> = ({ section }) => {
  const { formatMessage } = useIntl();
  const classes = useStyles({});
  const { memberId } = useParams<{ memberId: string }>();
  const history = useHistory();
  const accountId = useActiveAccountId();
  const hasFeature = useFeatures();
  const fountainLifeUXEnabled = hasFeature('fountainLifeUX');
  const isMasked = useMaskedMode();
  const mrnConfig = useSelector(selectActiveProject)?.mrnConfig;
  const { searchValue, registerSuggestions, unregisterSuggestions } =
    useOmnibarContext();
  const [showZoriChat, setShowZoriChat] = React.useState(false);
  const [chatWidth, setChatWidth] = React.useState(420);
  const [isChatFullscreen, setIsChatFullscreen] = React.useState(false);
  const [chatMode, setChatMode] = React.useState<'clinician' | 'member'>(
    'clinician',
  );

  React.useEffect(() => {
    if (!section && memberId) {
      const storedSection =
        (window.localStorage.getItem(
          MEMBER_SECTION_STORAGE_KEY,
        ) as MemberSection) || 'annual-report';
      history.replace(`${urls.app.members(memberId)}/${storedSection}`);
    }
  }, [history, memberId, section]);

  React.useEffect(() => {
    if (section) {
      window.localStorage.setItem(MEMBER_SECTION_STORAGE_KEY, section);
    }
  }, [section]);

  React.useEffect(() => {
    if (!isChatFullscreen) {
      return;
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsChatFullscreen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isChatFullscreen]);

  const handleOptOut = React.useCallback(() => {
    try {
      window.localStorage.removeItem(OPT_IN_STORAGE_KEY);
    } catch (error) {
      // Ignore storage failures; opting out is best-effort.
    }
    history.push(urls.app.patients(memberId));
  }, [history, memberId]);

  const {
    member,
    memberDisplayName,
    formattedBirthDate,
    elationChartUrl,
    isLoading,
    canSync,
    refreshEhrData,
  } = useMemberDetailData({
    accountId,
    memberId,
    isMasked,
    mrnConfig,
    formatMessage,
  });

  useMemberDetailOmnibarSuggestions({
    fountainLifeUXEnabled,
    memberId,
    memberDisplayName,
    section,
    formatMessage,
    searchValue,
    registerSuggestions,
    unregisterSuggestions,
    setShowZoriChat,
  });

  if (!section) {
    return null;
  }

  const contentMessage = (() => {
    switch (section) {
      default:
        return memberDetailMessages.contentPlaceholder;
    }
  })();

  const getMemberSectionUrl = (targetSection: MemberSection) =>
    `${urls.app.members(memberId)}/${targetSection}`;

  const sectionLabel = (() => {
    switch (section) {
      case 'annual-report':
        return formatMessage(memberDetailMessages.annualReportLabel);
      case 'action-plan':
        return formatMessage(memberDetailMessages.actionPlanLabel);
      case 'ai-tools':
        return formatMessage(memberDetailMessages.aiToolsLabel);
      case 'labs':
        return formatMessage(memberDetailMessages.labsLabel);
      case 'surveys':
        return formatMessage(memberDetailMessages.surveysLabel);
      case 'documents':
        return formatMessage(memberDetailMessages.documentsLabel);
      case 'omics-explorer':
        return formatMessage(memberDetailMessages.omicsExplorerLabel);
      default:
        return '';
    }
  })();

  const loadingFallback = isLoading ? (
    <Box fullWidth fullHeight align="center" justify="center">
      <DotLoader />
    </Box>
  ) : (
    <Text size="subbody">
      {formatMessage(memberDetailMessages.unknownMember)}
    </Text>
  );

  const menuItems = [
    <MenuItem
      key="annual-report"
      onClick={() => history.push(getMemberSectionUrl('annual-report'))}
      className={
        section === 'annual-report' ? classes.menuItemSelected : undefined
      }
    >
      {formatMessage(memberDetailMessages.annualReport)}
    </MenuItem>,
    <MenuItem
      key="action-plan"
      onClick={() => history.push(getMemberSectionUrl('action-plan'))}
      className={
        section === 'action-plan' ? classes.menuItemSelected : undefined
      }
    >
      {formatMessage(memberDetailMessages.actionPlan)}
    </MenuItem>,
    <MenuItem
      key="ai-tools"
      onClick={() => history.push(getMemberSectionUrl('ai-tools'))}
      className={section === 'ai-tools' ? classes.menuItemSelected : undefined}
    >
      {formatMessage(memberDetailMessages.aiTools)}
    </MenuItem>,
    <MenuItem
      key="labs"
      onClick={() => history.push(getMemberSectionUrl('labs'))}
      className={section === 'labs' ? classes.menuItemSelected : undefined}
    >
      {formatMessage(memberDetailMessages.labsAndData)}
    </MenuItem>,
    <MenuItem
      key="documents"
      onClick={() => history.push(getMemberSectionUrl('documents'))}
      className={section === 'documents' ? classes.menuItemSelected : undefined}
    >
      {formatMessage(memberDetailMessages.documents)}
    </MenuItem>,
    <MenuItem
      key="surveys"
      onClick={() => history.push(getMemberSectionUrl('surveys'))}
      className={section === 'surveys' ? classes.menuItemSelected : undefined}
    >
      {formatMessage(memberDetailMessages.surveys)}
    </MenuItem>,
  ];
  menuItems.push(
    <EnforceAccountFeatureToggle accountFeature="genomics" key="omics-explorer">
      <MenuItem
        onClick={() => history.push(getMemberSectionUrl('omics-explorer'))}
        className={
          section === 'omics-explorer' ? classes.menuItemSelected : undefined
        }
      >
        {formatMessage(memberDetailMessages.omicsExplorer)}
      </MenuItem>
    </EnforceAccountFeatureToggle>,
  );

  let chatWrapperClassName = classes.chatWrapper;
  if (isChatFullscreen) {
    chatWrapperClassName = `${classes.chatWrapper} ${classes.chatWrapperFullscreen}`;
  } else if (
    section === 'annual-report' ||
    section === 'labs' ||
    section === 'omics-explorer'
  ) {
    chatWrapperClassName = `${classes.chatWrapper} ${classes.chatWrapperOverlay}`;
  }

  const chatModeSelector = (
    <RadioGroupMinimal
      aria-label="Chat Selector"
      name="memberDetailChatMode"
      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
        setChatMode(event.target.value as 'clinician' | 'member');
      }}
      value={chatMode}
    >
      <Radio
        value="clinician"
        label={formatMessage(memberDetailMessages.chatClinicianLabel)}
      />
      <Radio
        value="member"
        label={formatMessage(memberDetailMessages.chatMemberHistoryLabel)}
      />
    </RadioGroupMinimal>
  );

  let chatContent = null;
  if (member) {
    chatContent =
      chatMode === 'clinician' ? (
        <Chat patient={member} />
      ) : (
        <MemberChatHistory patient={member} />
      );
  }

  return (
    <PageLayout
      maxWidth
      applyBackgroundCover
      classes={{
        root: classes.pageRoot,
        layoutContainer: classes.layoutContainer,
        headerBackgroundPadding: classes.headerBackgroundPadding,
        backgroundCover: classes.backgroundCover,
        content: classes.contentArea,
      }}
      left={
        <Box className={classes.header}>
          {isLoading ? (
            <Text size="subbody" weight="bold" className={classes.headerName}>
              {formatMessage(memberDetailMessages.loadingMember)}
            </Text>
          ) : (
            <>
              <Text size="subbody" weight="bold" className={classes.headerName}>
                {memberDisplayName}
              </Text>
              {(formattedBirthDate || elationChartUrl) && (
                <Box className={classes.headerMeta}>
                  {formattedBirthDate && (
                    <Text size="subbody" className={classes.headerDob}>
                      {formattedBirthDate}
                    </Text>
                  )}
                  {elationChartUrl && (
                    <Link
                      className={classes.elationLink}
                      to={elationChartUrl}
                      newTab
                    >
                      <span className={classes.elationLinkContent}>
                        {formatMessage(memberDetailMessages.elationLink)}
                        <ExternalLink style={{ width: 14, height: 14 }} />
                      </span>
                    </Link>
                  )}
                </Box>
              )}
            </>
          )}
        </Box>
      }
      headerCenter={
        <Box className={classes.menuActions}>
          <Menu
            aria-label={formatMessage(memberDetailMessages.menuLabel)}
            anchorElement={
              <ButtonUnstyled
                aria-label={formatMessage(memberDetailMessages.menuLabel)}
                className={classes.menuButton}
              >
                <MenuIcon />
                <Text size="caption" className={classes.menuLabelText}>
                  {sectionLabel ||
                    formatMessage(memberDetailMessages.menuLabel)}
                </Text>
              </ButtonUnstyled>
            }
            placement="bottom"
            items={menuItems}
          />
        </Box>
      }
      headerActions={
        <Box className={classes.headerActions}>
          {fountainLifeUXEnabled && (
            <Button variant="text" onClick={handleOptOut}>
              {formatMessage(memberDetailMessages.optOutNewView)}
            </Button>
          )}
          <Button
            variant="outlined"
            icon={RefreshCw}
            onClick={canSync ? refreshEhrData : undefined}
            disabled={!canSync}
          >
            Sync
          </Button>
          <Button
            variant={showZoriChat ? 'contained' : 'outlined'}
            icon={SparkleCircle}
            data-testid="member-detail-zori-chat-toggle"
            onClick={() => {
              setShowZoriChat(prev => {
                const next = !prev;
                if (!next) {
                  setIsChatFullscreen(false);
                }
                return next;
              });
            }}
          >
            Zori
          </Button>
        </Box>
      }
    >
      <Box className={classes.contentLayout}>
        <Box
          className={classes.mainContent}
          data-testid={`member-section-${section}`}
        >
          {section === 'annual-report' ? (
            <AnnualReportContent
              member={member}
              memberId={memberId}
              fallback={loadingFallback}
            />
          ) : section === 'action-plan' ? (
            <ActionPlanContent
              member={member}
              memberId={memberId}
              fallback={loadingFallback}
            />
          ) : section === 'labs' ? (
            <LabsContent member={member} fallback={loadingFallback} />
          ) : section === 'ai-tools' ? (
            <ZoriReportsContent
              member={member}
              memberId={memberId}
              fallback={loadingFallback}
            />
          ) : section === 'documents' ? (
            <DocumentsContent member={member} fallback={loadingFallback} />
          ) : section === 'surveys' ? (
            <SurveysContent member={member} fallback={loadingFallback} />
          ) : section === 'omics-explorer' ? (
            <OmicsExplorerContent memberId={memberId} />
          ) : (
            <Text size="subbody">{formatMessage(contentMessage)}</Text>
          )}
        </Box>
        {showZoriChat && member && (
          <Box className={chatWrapperClassName}>
            {isChatFullscreen ? (
              <div className={classes.chatResizableFullscreen}>
                <Box
                  className={`${classes.chatSidebar} ${classes.chatSidebarFullscreen}`}
                >
                  <Box
                    className={classes.chatHeader}
                    align="center"
                    justify="space-between"
                    paddingX={2}
                    paddingY={1}
                  >
                    <Box align="center" gap={1}>
                      <Text size="subbody" weight="bold">
                        Zori
                      </Text>
                    </Box>
                    {chatModeSelector}
                    <Box align="center" gap={1}>
                      {fountainLifeUXEnabled && (
                        <IconButton
                          size={0}
                          aria-label="Collapse"
                          icon={Minimize}
                          onClick={() => setIsChatFullscreen(prev => !prev)}
                        />
                      )}
                      <IconButton
                        size={0}
                        aria-label="Close Zori"
                        icon={X}
                        onClick={() => setShowZoriChat(false)}
                      />
                    </Box>
                  </Box>
                  <Box className={classes.chatContent}>
                    <AttachmentViewerProvider>
                      {chatContent}
                    </AttachmentViewerProvider>
                  </Box>
                </Box>
              </div>
            ) : (
              <Resizable
                width={chatWidth}
                height={0}
                onResize={(_, { size }) => setChatWidth(size.width)}
                resizeHandles={['w']}
                minConstraints={[320, 0]}
                maxConstraints={[640, 0]}
                handle={<div className={classes.resizeHandle} />}
              >
                <div
                  className={classes.chatResizable}
                  style={{ width: chatWidth }}
                >
                  <Box className={classes.chatSidebar}>
                    <Box
                      className={classes.chatHeader}
                      align="center"
                      justify="space-between"
                      paddingX={2}
                      paddingY={1}
                    >
                      <Box align="center" gap={1}>
                        <Text size="subbody" weight="bold">
                          Zori
                        </Text>
                      </Box>
                      {chatModeSelector}
                      <Box align="center" gap={1}>
                        {fountainLifeUXEnabled && (
                          <IconButton
                            size={0}
                            aria-label="Expand"
                            icon={Maximize}
                            onClick={() => setIsChatFullscreen(true)}
                          />
                        )}
                        <IconButton
                          size={0}
                          aria-label="Close Zori"
                          icon={X}
                          onClick={() => setShowZoriChat(false)}
                        />
                      </Box>
                    </Box>
                    <Box className={classes.chatContent}>
                      <AttachmentViewerProvider>
                        {chatContent}
                      </AttachmentViewerProvider>
                    </Box>
                  </Box>
                </div>
              </Resizable>
            )}
          </Box>
        )}
      </Box>
    </PageLayout>
  );
};

MemberDetail.displayName = 'MemberDetail';
export default MemberDetail;
